import { DocumentReviewPatch } from "@/types/document/documentReviewerPatch";
import { ActionResult } from "@/store/modules/document/document";
import {
    Controllers,
    DocumentField,
    IsBadRequestResult,
    IsForbiddenResult,
    IsNotFoundResult,
    IsServerErrorResult,
    ProblemResult
} from "@infoland/iprova-api";
import storeHelper from "@/store/store-helper";
import { DocumentReviewer } from "@/types/document/documentReviewer";

export async function updateDocumentReviewAsync(
    documentId: string,
    versionNumber: number,
    reviewerId: string,
    documentReviewPatch: DocumentReviewPatch
): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.patchAsync<DocumentReviewPatch, void | ProblemResult<string>>(
        `documents/${documentId}/v${versionNumber}/reviewers/${reviewerId}`,
        documentReviewPatch
    );
    const returnValue = handleDocumentReviewMutationResult(result);

    return returnValue;
}

function handleDocumentReviewMutationResult(result: void | ProblemResult<string>): ActionResult<void, string> {
    if (IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarServerErrorMessage(result);
        return { success: false };
    } else if (IsForbiddenResult(result)) {
        if (result.extrainfo) {
            storeHelper.siteStore.setSnackBarMessageLookup("document.review.errorMessages." + result.extrainfo, true);
        } else {
            storeHelper.siteStore.setSnackBarMessageLookup("document.review.errorMessages.forbidden", true);
        }

        return { success: false, errorData: result.extrainfo };
    } else if (IsBadRequestResult(result) || IsNotFoundResult(result)) {
        if (result.extrainfo) {
            storeHelper.siteStore.setSnackBarMessageLookup("document.review.errorMessages." + result.extrainfo, true);
        }
        return { success: false };
    } else return { success: true };
}

export async function getDocumentReviewersAsync(
    documentId: string,
    versionNumber: number,
    reviewerIds: string[] = null,
    active: boolean = null,
    includeReviewRound: boolean = false
): Promise<DocumentReviewer[]> {
    let path = `documents/${documentId}/v${versionNumber}/reviewers`;
    let reviewerParameter = "";
    if (reviewerIds) {
        reviewerIds.forEach(reviewerId => (reviewerParameter += `${reviewerId},`));
    }

    if (reviewerParameter.length > 0) {
        path += "?" + "user_ids=" + reviewerParameter.substring(0, reviewerParameter.length - 1);
    }

    if (active) {
        path += (path.indexOf("?") < 0 ? "?" : "&") + "active=" + active;
    }

    if (includeReviewRound) {
        path += (path.indexOf("?") < 0 ? "?" : "&") + "include_review_round=" + includeReviewRound;
    }

    const result = await Controllers.GenericController.getAsync<DocumentReviewer[]>(path);
    return result;
}

export async function getDocumentFieldsAsync(
    documentId: string,
    state: string,
    shown_to_readers?: boolean,
    include_meta_field_type?: boolean
): Promise<DocumentField[]> {
    // gets the document content
    let path: string = `documents/${documentId}/${state}/fields`;
    let queryParams: string = getQueryParam("shown_to_readers", shown_to_readers) + getQueryParam("include_meta_field_type", include_meta_field_type);
    path += "?" + queryParams.substring(1);
    return await Controllers.GenericController.getAsync<DocumentField[]>(path);
}

function getQueryParam(key: string, value: string | number | boolean): string {
    if (!value) {
        return "";
    }

    return `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}
